import SensorList from "./SensorList";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import SensorDetail from "./SensorDetail";
import SensorForm from "./Form/SensorForm";
import NoSelected from "@components/NoSelected";
import Spinner from "@components/Spinner/Spinner";
//import AddButton from "@components/Button/AddButton";
import { Container, Row, Col, Offcanvas } from "react-bootstrap";
import ServerError from "@components/Error/ServerError";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import EmptyTree from "@modules/Trees/components/EmptyTree";
import { usePaginatedSensors } from "@hooks/usePaginatedData";
import { useBackgroundTrees } from "@hooks/useBackgroundData";
import { useUrlListState } from "@hooks/useUrlListState";
import { useProjectFilter } from "../../../context/ProjectFilterContext";
import useWindowDimensions from "@hooks/useWindowDimension";
import useMatchedElementHeight from "@hooks/useMatchedElementHeight";
//import TableActions from "@components/TableActions";

export default function SensorPage() {
  const { mobile } = useSelector((state) => state.mobile);
  const [isUpdate, setIsUpdate] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [detail, setDetail] = useState();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = query.get("selectedSensor");
  const fromDashboard = query.get("from") === "dashboard";
  const { selectedProjectId } = useProjectFilter();
  const { trees: allTrees } = useBackgroundTrees();
  const urlListConfig = React.useMemo(
    () => ({
      defaults: {
        page: 1,
        pageSize: 20,
        search: "",
        ordering: "",
        area: [],
      },
      intKeys: ["page", "pageSize"],
      arrayKeys: ["area"],
    }),
    []
  );
  const { state: listState, setValue: setListValue } = useUrlListState(urlListConfig);
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const useRightDrawer = width < 992;
  const listColumnRef = React.useRef(null);
  const detailHeight = useMatchedElementHeight(
    listColumnRef,
    ".table-wrapper",
    !mobile && id && !useRightDrawer
  );
  const { page, pageSize, search, ordering, area } = listState;

  // Reset to page 1 when project filter changes
  React.useEffect(() => {
    setListValue("page", 1);
  }, [selectedProjectId]);

  const {
    data: sensorResponse,
    isLoading,
    isError,
  } = usePaginatedSensors({
    page,
    pageSize,
    search,
    projectId: selectedProjectId,
    ordering,
    area,
  });

  const sensorData = sensorResponse?.results || [];
  const totalCount = sensorResponse?.count || 0;
  const hasNext = !!sensorResponse?.next;
  const hasPrevious = !!sensorResponse?.previous;
  const areaOptions = React.useMemo(() => {
    const sourceTrees = selectedProjectId
      ? allTrees.filter((tree) => String(tree.project_id) === String(selectedProjectId))
      : allTrees;
    const byId = new Map();
    sourceTrees.forEach((tree) => {
      const id = String(tree.area_id || tree?.area?.id || "");
      const name = tree.area_name || tree?.area?.name || "";
      if (!id || !name || byId.has(id)) return;
      byId.set(id, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allTrees, selectedProjectId]);

  if (isLoading && !sensorResponse) return <Spinner height={"80vh"} />;
  if (isError) return <ServerError />;

  const closeSensorDrawer = () => {
    const params = new URLSearchParams(location.search);
    params.delete("selectedSensor");
    navigate(`/sensors${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <>
      {fromDashboard && <Breadcrumb backLink="/dashboard" />}

      <Container className={!mobile ? "entity-list-page" : undefined} data-tour="sensor-page">
        {totalCount === 0 && !search ? (
          <>{<EmptyTree width={100} title={"project_details.empty_sensors"} />}</>
        ) : (
          <Row className="g-3">
            <Col
              ref={listColumnRef}
              lg={useRightDrawer ? 12 : id ? 6 : 12}
              data-tour="sensor-list"
              className="entity-master-column"
            >
              <SensorList
                sensors={sensorData}
                setDetail={setDetail}
                setIsUpdate={setIsUpdate}
                setOpenForm={setOpenForm}
                page={page}
                setPage={(value) => setListValue("page", value)}
                pageSize={pageSize}
                setPageSize={(value) => setListValue("pageSize", value)}
                search={search}
                setSearch={(value) => setListValue("search", value)}
                ordering={ordering}
                setOrdering={(value) => setListValue("ordering", value)}
                selectedAreaIds={area}
                setSelectedAreaIds={(value) => setListValue("area", value)}
                totalCount={totalCount}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                isLoading={isLoading}
                areaOptions={areaOptions}
                /*buttons={({ isNarrow }) => (
                      <TableActions
                        onAdd={() => setOpenForm(true)}
                        addLabelKey="button.add_sensor"
                        addTestId="add-sensor-button"
                        collapseSecondary={isNarrow}
                        secondary={[]}
                      />
                    )}*/
              />
            </Col>
            {id && !useRightDrawer && (
              <Col lg="6" className="sensor-detail-column entity-detail-column">
                {!id ? (
                  <NoSelected text="sensor_details.no_sensor_selected" />
                ) : (
                  <div
                    className="entity-detail-height-shell"
                    style={detailHeight ? { height: `${detailHeight}px` } : undefined}
                  >
                    <SensorDetail setIsUpdate={setIsUpdate} setOpenForm={setOpenForm} />
                  </div>
                )}
              </Col>
            )}
          </Row>
        )}
      </Container>
      {useRightDrawer && (
        <Offcanvas
          show={Boolean(id)}
          onHide={closeSensorDrawer}
          placement="end"
          backdrop={false}
          scroll={false}
          style={{
            width: "min(92vw, 560px)",
            top: "4rem",
            height: "calc(100dvh - 4rem)",
            zIndex: 1020,
          }}
        >
          <Offcanvas.Body className="p-0">
            <SensorDetail setIsUpdate={setIsUpdate} setOpenForm={setOpenForm} />
          </Offcanvas.Body>
        </Offcanvas>
      )}
      <SensorForm
        isUpdate={isUpdate}
        detail={detail}
        setIsUpdate={setIsUpdate}
        show={openForm}
        setOpenForm={setOpenForm}
      />
    </>
  );
}
